/**
 * <accordion-component> — UN accordéon (un titre, un contenu pliable).
 *
 * Pour avoir plusieurs accordéons côte à côte, on place plusieurs balises
 * <accordion-component> les unes à la suite des autres.
 *
 * Structure interne (pas de <details>/<summary> car non animables) :
 *
 *   <accordion-component data-open="false">
 *     <button type="button" class="accordion__trigger" aria-expanded="false">
 *       <span>Titre</span>
 *       <!-- icônes +/− optionnelles -->
 *     </button>
 *     <div class="accordion__panel">
 *       <div class="accordion__panel-inner">contenu</div>
 *     </div>
 *   </accordion-component>
 *
 * Le CSS anime le height via le trick `grid-template-rows: 0fr → 1fr` :
 *   .accordion__panel       { display: grid; grid-template-rows: 0fr; transition: 300ms; }
 *   accordion-component[data-open="true"] > .accordion__panel { grid-template-rows: 1fr; }
 *   .accordion__panel-inner { min-height: 0; overflow: hidden; }
 *
 * Attributs data-* (toutes optionnels) :
 *   data-open="true"          État initial ouvert. Défaut: "false".
 *   data-default-open="true"  Alias historique de data-open="true".
 *   data-disable-above="749"  Au-dessus de cette largeur (px), le toggle est
 *                             bloqué et l'accordéon est forcé ouvert
 *                             (cas typique du footer mobile-only).
 *   data-accordion-group="x"  Mode exclusif : ouvrir cet accordéon referme
 *                             automatiquement les autres accordéons de la page
 *                             portant la même valeur de groupe.
 */
(function () {
  if (customElements.get("accordion-component")) return;

  class AccordionComponent extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector(":scope > .accordion__trigger");
      this.panel = this.querySelector(":scope > .accordion__panel");
      if (!this.trigger || !this.panel) return;

      // Lie trigger ↔ panel pour les lecteurs d'écran.
      if (!this.panel.id) {
        this.panel.id = `accordion-panel-${Math.random().toString(36).slice(2, 9)}`;
      }
      this.trigger.setAttribute("aria-controls", this.panel.id);
      this.panel.setAttribute("role", "region");
      this.panel.setAttribute("aria-labelledby", this.trigger.id || this.#ensureId(this.trigger));

      this.trigger.addEventListener("click", (e) => this.#handleClick(e));

      this.disableAbove =
        this.dataset.disableAbove != null ? parseInt(this.dataset.disableAbove, 10) : null;
      if (this.disableAbove != null) {
        this.mq = window.matchMedia(`(max-width: ${this.disableAbove}px)`);
        this.mqListener = () => this.#applyState();
        if (this.mq.addEventListener) this.mq.addEventListener("change", this.mqListener);
        else this.mq.addListener(this.mqListener);
      }

      // État initial : data-open inline si présent, sinon data-default-open,
      // sinon fermé.
      if (!this.dataset.open) {
        this.dataset.open = this.dataset.defaultOpen === "true" ? "true" : "false";
      }
      this.#applyState();
    }

    disconnectedCallback() {
      if (!this.mq) return;
      if (this.mq.removeEventListener) this.mq.removeEventListener("change", this.mqListener);
      else this.mq.removeListener(this.mqListener);
    }

    #ensureId(el) {
      if (!el.id) el.id = `accordion-trigger-${Math.random().toString(36).slice(2, 9)}`;
      return el.id;
    }

    /** Vrai si au-dessus du breakpoint (toggle bloqué, forcé ouvert). */
    #isDisabled() {
      if (this.disableAbove == null) return false;
      return !this.mq.matches;
    }

    #setOpen(open) {
      this.dataset.open = open ? "true" : "false";
      this.trigger.setAttribute("aria-expanded", open ? "true" : "false");
    }

    #applyState() {
      if (this.#isDisabled()) {
        this.#setOpen(true);
      } else {
        this.#setOpen(this.dataset.open === "true");
      }
    }

    /** Ferme l'accordéon. API publique appelée par le mode groupe exclusif. */
    close() {
      if (this.#isDisabled()) return;
      this.#setOpen(false);
    }

    #handleClick(event) {
      if (this.#isDisabled()) {
        event.preventDefault();
        return;
      }
      const willOpen = this.dataset.open !== "true";
      this.#setOpen(willOpen);
      // Mode exclusif : à l'ouverture, referme les autres du même groupe.
      if (willOpen) this.#closeGroupSiblings();
    }

    /**
     * Si l'accordéon porte data-accordion-group, referme tous les autres
     * accordéons de la page partageant la même valeur de groupe.
     */
    #closeGroupSiblings() {
      const group = this.dataset.accordionGroup;
      if (!group) return;
      const members = document.querySelectorAll(
        `accordion-component[data-accordion-group="${CSS.escape(group)}"]`,
      );
      members.forEach((el) => {
        if (el !== this && typeof el.close === "function") el.close();
      });
    }
  }

  customElements.define("accordion-component", AccordionComponent);
})();
